import type { paths } from "@/lib/api/generated/api";

/**
 * The chat wire shapes, taken from the generated OpenAPI document.
 *
 * NOTHING IS RETYPED BY HAND except the two upload steps at the bottom, and the comment there
 * says why. A DTO copied into the client is a DTO that stops matching the server the first time
 * a field is renamed, and the mismatch shows up as an empty bubble rather than as a build error.
 */

type ConversationListResponse =
  paths["/api/v1/chat/conversations"]["get"]["responses"][200]["content"]["application/json"];

/** A row of the rail: summary, unread counter, a five-member preview — never the full roster. */
export type ChatConversationRow = ConversationListResponse["data"][number];

type ConversationDetailResponse =
  paths["/api/v1/chat/conversations/{id}"]["get"]["responses"][200]["content"]["application/json"];

/** The same row plus `allMembers` and `createdByUserId`. Only the detail endpoint returns it. */
export type ChatConversationDetail = ConversationDetailResponse["data"];

export type ChatConversationMember = ChatConversationDetail["allMembers"][number];

type MessagesResponse =
  paths["/api/v1/chat/conversations/{id}/messages"]["get"]["responses"][200]["content"]["application/json"];

export type ChatMessageRow = MessagesResponse["data"][number];

export type ChatAttachmentRow = ChatMessageRow["attachments"][number];

type UnreadResponse =
  paths["/api/v1/chat/unread-count"]["get"]["responses"][200]["content"]["application/json"];

export type ChatUnreadCount = UnreadResponse["data"];

type ReadReceiptResponse =
  paths["/api/v1/chat/conversations/{id}/read"]["post"]["responses"][200]["content"]["application/json"];

/** `{ ok, notificationsCleared }` — how many of the reader's bell rows the read settled (H4). */
export type ChatReadReceipt = ReadReceiptResponse["data"];

type SearchResponse =
  paths["/api/v1/chat/messages/search"]["get"]["responses"][200]["content"]["application/json"];

/** A message the server found for a term, with the conversation it is in named (H6). */
export type ChatSearchHit = SearchResponse["data"][number];

/** A SYSTEM line's event (H9) — `null` on every message somebody wrote. */
export type ChatSystemEvent = NonNullable<ChatMessageRow["system"]>;

export type ChatMemberRole = "OWNER" | "MEMBER";

type AttachmentDownloadResponse =
  paths["/api/v1/chat/messages/{id}/attachments/{linkId}/download"]["get"]["responses"][200]["content"]["application/json"];

export type ChatAttachmentDownload = AttachmentDownloadResponse["data"];

type PeopleResponse =
  paths["/api/v1/directory/people"]["get"]["responses"][200]["content"]["application/json"];

/**
 * A person, as the directory lists them.
 *
 * Read straight from the generated document rather than imported from the directory feature:
 * the picker needs four fields and a cross-feature import would make the chat screen fail to
 * build whenever the directory module is mid-change. The endpoint is the contract, not the
 * neighbouring folder.
 */
export type DirectoryPersonRow = PeopleResponse["data"][number];

export interface ChatConversationFilters {
  page?: number;
  limit?: number;
  q?: string;
  includeArchived?: string;
}

/**
 * The upload intent, hand-written — the ONE exception in this file.
 *
 * `POST /files/upload-intent` is declared in the OpenAPI document with `content?: never` on its
 * 200/201, so the generated types carry no body at all for it. §9 of BACKEND_CONTRACT states
 * the shape; this is that statement, narrowed to what the chat composer uses. If the generator
 * ever learns the response, delete this and derive it like everything above.
 */
export interface UploadIntent {
  fileId: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresAt: string;
}

/** Permission keys, named once so a typo is a compile error at exactly one site. */
export const CHAT_PERMISSIONS = {
  read: "chat.conversation.read",
  send: "chat.message.send",
  manage: "chat.conversation.manage",
} as const;

export const CHAT_MODULE = "module.chat";

/**
 * One entry of `GET /files/purposes` — the upload-slot catalogue.
 *
 * Hand-written for the same reason `UploadIntent` above is: the endpoint is declared in the
 * OpenAPI document without a response body. §9 states the shape, and the notes and directory
 * features read the same catalogue with the same fields.
 */
export interface AttachmentPurpose {
  key: string;
  label: string;
  entityTypes: string[];
  mimeTypes: string[];
  maxBytes: number;
  maxPerEntity: number;
}

/** The purpose and subject a chat attachment is uploaded under. Fixed by the contract, §9. */
export const CHAT_ATTACHMENT_PURPOSE = "chat_attachment";
export const CHAT_ATTACHMENT_ENTITY_TYPE = "chat.message";

/**
 * The FALLBACK limits, not the rule.
 *
 * §9 says the numbers in the contract table are an order of magnitude and that
 * `GET /files/purposes` is where a client learns the slot it may upload into. So these are used
 * only while the catalogue has not answered (or answered without our slot): hard-coding them as
 * the rule would start refusing files the server would have taken the day somebody raises the
 * limit, and the person would have no way to tell which side said no.
 */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const ATTACHMENT_MAX_COUNT = 10;

/**
 * §14b: a message body is 1–8000 characters of plain text.
 *
 * BOTH ENDS ARE CHECKED BEFORE THE REQUEST. `sendMessageSchema` types the body as
 * `z.string().trim().min(1).max(8000)`, so an attachment-only send and a pasted 9 000-character
 * body are both 400s — and a 400 on a send is not a recoverable state here, because the retry
 * replays the same body under the same idempotency key forever.
 */
export const MESSAGE_MIN_LENGTH = 1;
export const MESSAGE_MAX_LENGTH = 8000;
